import React from 'react';

import { Check, Close } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';

type VoucherEvaluationResult = {
  conditionResultText?: string | null;
  code?: string | null;
  checkPassed?: boolean;
  passed?: boolean;
};

type VoucherEvaluationGroup = {
  rewardTitle?: string | null;
  title?: string | null;
  rewardGroup?: {
    title?: string | null;
    results?: Array<VoucherEvaluationResult>;
  };
  results?: Array<VoucherEvaluationResult>;
};

type VoucherLogLike = {
  metadata?: unknown;
  evaluations?: unknown;
};

function getVoucherEvaluationGroups(row: VoucherLogLike): Array<VoucherEvaluationGroup> {
  const metadata = row.metadata as
    | {
        evaluations?: unknown;
        voucherEvaluations?: unknown;
      }
    | undefined;
  const evaluations = metadata?.evaluations ?? row.evaluations ?? metadata?.voucherEvaluations ?? [];
  return Array.isArray(evaluations) ? evaluations : [];
}

export default function VoucherEvaluationSummary({ row }: { row: VoucherLogLike }) {
  const evaluations = getVoucherEvaluationGroups(row);

  if (evaluations.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Stack spacing={0.75} sx={{ width: '100%', minWidth: 0, py: 0.5 }}>
      {evaluations.map((evaluation, evaluationIndex) => {
        const title = evaluation.rewardTitle ?? evaluation.rewardGroup?.title ?? evaluation.title ?? 'Evaluation';
        const results = evaluation.rewardGroup?.results ?? evaluation.results ?? [];

        return (
          <Stack key={`${title}-${evaluationIndex}`} spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} noWrap title={title}>
              {title}
            </Typography>
            <Stack spacing={0.25} sx={{ pl: 0.5 }}>
              {results.map((result, resultIndex) => {
                const passed = result.checkPassed ?? result.passed ?? false;
                const label = result.conditionResultText ?? result.code ?? 'Evaluation result';

                return (
                  <Stack
                    key={`${title}-${evaluationIndex}-${result.code ?? resultIndex}`}
                    direction="row"
                    spacing={0.5}
                    alignItems="flex-start"
                    sx={{ minWidth: 0 }}
                  >
                    {passed ? (
                      <Check color="success" fontSize="inherit" sx={{ mt: '2px', flexShrink: 0 }} />
                    ) : (
                      <Close color="error" fontSize="inherit" sx={{ mt: '2px', flexShrink: 0 }} />
                    )}
                    <Typography
                      variant="body2"
                      color={passed ? 'success.main' : 'error.main'}
                      sx={{ minWidth: 0, wordBreak: 'break-word' }}
                    >
                      {label}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
}
